<template>
  <div class="flex flex-row flex-wrap w-10">
    <HeadTitle
      title="Detalle de configuración de carta"
      description="Formulario de creacion y configuración de una carta en donde se definen los parametros de generación y el alcance de la misma"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" class="mt-2" />
  </div>
  <p></p>
  <br />
  <Step :stepNumber="1" title="Configuración basica">
    <div class="formgrid grid w-12 md:w-6">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="name">Nombre <span class="text-red-600">*</span></label>
        <InputText
          v-model="name"
          id="name"
          type="text"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          nameError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText
          id="description"
          v-model="description"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="generationType"
          >Método de generación <span class="text-red-600">*</span></label
        >
        <Dropdown
          id="generationType"
          v-model="generationType"
          :options="approvalMethodsOpts"
          optionLabel="label"
          optionValue="value"
          :disabled="!isEditMode"
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          generationTypedError || '&nbsp;'
        }}</small>
      </div>
      <div
        class="field col-10 md:col-6 flex flex-column"
        v-if="generationType === 'WITH_APPROVAL'"
      >
        <label for="approver"
          >Seleccionar Aprobador <span class="text-red-600">*</span></label
        >
        <Dropdown
          v-model="approver"
          :options="approvers"
          optionLabel="name"
          optionValue="id"
          filter
          :disabled="!isEditMode"
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          approverError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="approver"
          >Seleccionar nóminas <span class="text-red-600">*</span></label
        >
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          :options="payrolls || []"
          v-model="selectedPayrolls"
          show-key="name"
          value-key="id"
          :required="true"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-8 flex flex-column">
        <div class="flex flex-row mb-2">
          <label for="approver">Correos a notificar</label>
          <InfoButton
            message="Se enviara una copia de la carta generada a los correos definidos en este campo"
          />
        </div>
        <Chips
          v-model="emailsToNotify"
          separator=","
          class="w-12"
          invalid
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Step :stepNumber="2" title="Preparar documentos">
    <Button
      label="Abrir plantilla"
      class="text-sm font-light"
      icon="pi pi-window-maximize"
      @click="isEditorModalVisible = !isEditorModalVisible"
      :disabled="!isEditMode"
    ></Button>
  </Step>
  <Footer>
    <template #left>
      <AuditButton
        :entities="['LetterCertifConfig']"
        :record-ids="[letterConfigId]"
      />
    </template>
    <ConfirmPopup></ConfirmPopup>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      class="text-sm font-light"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
    />
  </Footer>
  <Dialog
    v-model:visible="isEditorModalVisible"
    class="p-dialog-maximized"
    modal
  >
    <template #header>
      <HeadTitle
        title="Editor de carta"
        description="Editor de carta para personalizar el contenido de la carta"
      />
    </template>
    <div class="flex flex-column h-full">
      <Editor
        ref="letterEditorComponent"
        :template="template"
        :page-properties="{ margins: pageMargins }"
      />
    </div>
    <template #footer>
      <FooterEditor
        ref="footerEditRef"
        @on-preview="onPreview"
        @save-editor-content="onSaveEditorContent"
        :header-img="headerImg"
        :footer-img="footerImg"
      />
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import Step from '@/shared/components/Step.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import Approver from '@/modules/approvers/models/Approver'
  import { getApproversByCompanyId } from '@/modules/approvers/services/ApproverService'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import Payroll from '@/modules/payrolls-config/models/Payroll'
  import { getPayrollsByCompanyId } from '@/modules/payrolls-config/services/PayrollService'
  import {
    getLetterConfigById,
    getPreviewLetter,
    updateLetterConfig,
  } from '../services/LetterConfgServices'
  import router from '@/router'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import { ref, computed, onMounted, reactive, nextTick } from 'vue'
  import { useRoute } from 'vue-router'
  import IUpdateLetterConfig from '../services/dtos/IUpdateLetterConfig'
  import Editor from '../components/Editor.vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'
  import FooterEditor from '../components/FooterEditor.vue'

  const path = useRoute()
  const letterConfigId = ref()
  const companyStore = useCompany()
  const loadingStore = useLoading()
  const notificationStore = useNotifications()
  const { company } = storeToRefs(companyStore)
  const letterEditorComponent = ref<any>(null)
  const isEditorModalVisible = ref(false)
  const footerEditRef = ref()
  const headerImg = ref()
  const footerImg = ref()

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await fetchApprovers()
    await fetchPayrolls()
  })

  const approvalMethodsOpts = ref([
    { label: 'Requiere aprobación', value: 'WITH_APPROVAL' },
    { label: 'Automática', value: 'AUTOMATIC' },
  ])

  let pageMargins = reactive({
    top: 0.39 * 2,
    bottom: 0.39 * 2,
    left: 0.39 * 2,
    right: 0.39 * 2,
  })

  const approvers = ref<Approver[]>()
  const payrolls = ref<Payroll[]>()
  const isEditMode = ref<boolean>(false)

  const nameSchema = yup.string().required('El nombre es requerido')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const generationTypeSchema = yup
    .string()
    .required('El método de aprobación es requerido')
  const approverSchema = yup.string().required('El aprobador es requerido')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const emailsSchema = yup
    .array()
    .test('emails', 'Uno de los correos es invalido', (value) => {
      if (value) {
        const emails = value
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: name,
    errorMessage: nameError,
    validate: nameValidate,
  } = useField<string>('description', nameSchema)
  const {
    value: generationType,
    errorMessage: generationTypedError,
    validate: modValidate,
  } = useField<string>('generationType', generationTypeSchema)
  const {
    value: approver,
    errorMessage: approverError,
    validate: approverValidate,
  } = useField<string>('approver', approverSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)
  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string[]>('emailsToNotify', emailsSchema)
  const template = ref<string>('')

  onMounted(async () => {
    loadingStore.setLoadingState()
    const { id } = path.params
    letterConfigId.value = String(id)
    if (letterConfigId.value) {
      const letterConfig = await getLetterConfigById(letterConfigId.value)
      name.value = letterConfig.name
      description.value = letterConfig.description || ''
      generationType.value = letterConfig.generationType
      approver.value = letterConfig.approver?.id || ''
      selectedPayrolls.value = letterConfig.payrolls.map(
        (payroll: any) => payroll.id
      )
      emailsToNotify.value = letterConfig.emailsToNotify || []
      template.value = letterConfig.template || ''
      pageMargins = letterConfig.pageProperties?.margins || pageMargins
      headerImg.value = letterConfig.header || null
      footerImg.value = letterConfig.footer || null
      companyStore.setCompany({
        id: letterConfig.company.id,
        companyId: letterConfig.company.companyId,
        code: letterConfig.company.code,
        name: letterConfig.company.name,
        client: {
          id: letterConfig.company.client.id,
          code: letterConfig.company.client.code,
          name: letterConfig.company.client.name,
        },
      })
    }

    await fetchApprovers()
    await fetchPayrolls()
    loadingStore.dismissLoadingState()
  })

  const fetchApprovers = async () => {
    if (company.value?.id) {
      const reponse = await getApproversByCompanyId(company.value?.id)
      approvers.value = reponse
    } else {
      notificationStore.addErrorNotification({
        detail: `No se ha seleccionado una compañia`,
        severity: 'error',
        summary: `Error al cargar aprobadores`,
      })
    }
  }

  const fetchPayrolls = async () => {
    if (company.value?.id) {
      const response = await getPayrollsByCompanyId(company.value?.id || '')
      payrolls.value = payrolls.value = response.map((payroll) => ({
        ...payroll,
        name: payroll.code + ' - ' + payroll.name,
      }))
    } else {
      notificationStore.addErrorNotification({
        detail: `No se ha seleccionado una compañia`,
        severity: 'error',
        summary: `Error al cargar nóminas`,
      })
    }
  }

  const onSave = async () => {
    loadingStore.setLoadingState()
    const letterConfig: IUpdateLetterConfig = {
      id: String(path.params.id) || '',
      name: name.value,
      description: description.value,
      generationType: generationType.value,
      approverId: approver.value || undefined,
      companyId: company.value?.id || '',
      payrollsIds: selectedPayrolls.value,
      emailsToNotify: emailsToNotify.value,
      template: template.value,
      pageProperties: {
        margins: pageMargins,
      },
      footer: footerImg.value,
      header: headerImg.value,
    }
    const response = await updateLetterConfig(letterConfig)

    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Configuración de carta actualizada con exito`,
      })
    }
    loadingStore.dismissLoadingState()
    router.push('/letterConfigs')
  }

  const setEditorContent = () => {
    const { editorRef, margins } = letterEditorComponent.value
    const content = editorRef
    template.value = content
    pageMargins = {
      top: Number(margins.top) / 2.54,
      bottom: Number(margins.bottom) / 2.54,
      left: Number(margins.left) / 2.54,
      right: Number(margins.right) / 2.54,
    }
    headerImg.value = footerEditRef.value.headerImg
    footerImg.value = footerEditRef.value.footerImg
  }

  const onSaveEditorContent = () => {
    setEditorContent()
    isEditorModalVisible.value = false
  }

  const onPreview = async () => {
    loadingStore.setLoadingState()
    setEditorContent()
    await nextTick()
    const buffer = await getPreviewLetter({
      template: template.value,
      header: headerImg.value,
      footer: footerImg.value,
      pageProperties: {
        margins: pageMargins,
      },
    })
    loadingStore.dismissLoadingState()

    const blob = new Blob([buffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  const isValidToSave = computed(() => {
    return (
      isEditMode.value &&
      !nameError.value &&
      !descriptionError.value &&
      !generationTypedError.value &&
      !payrollsError.value &&
      !emailsError.value &&
      (generationType.value === 'AUTOMATIC' || approver.value)
    )
  })

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }
</script>
<style scoped>
  :deep(.p-button-label) {
    font-size: 0.8rem;
  }
</style>
